import { tap, map, catchError } from "rxjs/operators";
import { Wrapper } from "app/core/models/wrapper.model";
import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpHeaders, HttpContext } from "@angular/common/http";
import { SKIP_PROGRESS } from "app/core/interceptors/progress.context";
import { Router } from "@angular/router";
import { CookieService } from "ngx-cookie";
import { environment } from "environments/environment";
import { Observable, throwError } from "rxjs";

@Injectable()
export class AuthService {
  private http = inject(HttpClient);
  private route = inject(Router);
  private cookieService = inject(CookieService);

  private readonly menuRouteCacheResetKey = "menu_route_cache_reset";
  private readonly sessionStartKey = "session_start";
  // Item 4: duração máxima da sessão = 2 horas.
  private readonly sessionMaxMs = 2 * 60 * 60 * 1000;
  securityApi = `${environment.api}/svc-security/api`;

  // Modo de autenticacao por aplicacao de origem (migracao gradual).
  // 'new'   -> auth novo (OpenIddict / cfoab.services.auth) quando a origem manda authVersion=v2.
  // 'legacy'-> auth legado (IS4 IdentityServer, issuer .../security-auth) quando nao manda nada (default = compat retroativa).
  private readonly authModeKey = "oab-auth-mode";
  private readonly securityAuthLegacyApi = `${environment.api}${environment.security_auth_legacy_path}/api`;
  private readonly securityAuthLegacyToken = `${environment.api}${environment.security_auth_legacy_path}/connect/token`;

  // Resolve o modo de auth a partir da query string da origem.
  //
  // O proprio front de Seguranca faz parte do fluxo novo (authVersion=v2). Por isso usamos 'new'
  // quando o login e do proprio sistema; so permanece 'legacy' quando outro sistema legado delega
  // o login para ca SEM mandar authVersion=v2 (compat retroativa).
  //
  // Regras (em ordem):
  //   1) authVersion=v2 explicito                          -> 'new'
  //   2) sem callbackUrl (acesso direto a /pages/login)    -> 'new'  (login do proprio sistema)
  //   3) callbackUrl aponta para o proprio host de Seguranca -> 'new'
  //   4) callbackUrl aponta para outro sistema (legado)    -> 'legacy'
  resolveAuthMode(authVersion: unknown, callbackUrl?: unknown): "new" | "legacy" {
    if (String(authVersion).toLowerCase() === "v2") {
      return "new";
    }

    const rawCallback = typeof callbackUrl === "string" ? callbackUrl.trim() : "";
    if (!rawCallback) {
      return "new";
    }

    return this.isSelfCallback(rawCallback) ? "new" : "legacy";
  }

  // Determina se o callbackUrl aponta para o proprio front de Seguranca (mesmo host) ou para
  // um destino interno (caminho relativo). O callback chega em base64 (mesmo atob do redirect).
  private isSelfCallback(callbackUrl: string): boolean {
    let decoded = callbackUrl;
    try {
      decoded = atob(callbackUrl);
    } catch {
      // ja estava em texto puro
    }

    decoded = decoded.trim();
    if (!decoded) {
      return true;
    }

    // Caminho relativo (ex.: "/vincular-usuario") -> permanece no proprio front.
    if (decoded.startsWith("/") && !decoded.startsWith("//")) {
      return true;
    }

    try {
      const target = new URL(decoded, window.location.origin);
      return target.hostname.toLowerCase() === window.location.hostname.toLowerCase();
    } catch {
      // Nao e uma URL absoluta valida -> trata como destino interno do proprio sistema.
      return true;
    }
  }

  // Modo persistido no login. Usado por refresh/logout, que NAO recebem mais a query string.
  getAuthMode(): "new" | "legacy" {
    return localStorage.getItem(this.authModeKey) === "new" ? "new" : "legacy";
  }

  private setAuthMode(mode: "new" | "legacy"): void {
    localStorage.setItem(this.authModeKey, mode);
  }

  loginIsValid(
    username: string,
    password: string,
    authMode: "new" | "legacy" = "legacy",
    recaptchaToken?: string,
  ): Observable<Wrapper<any>> {
    // recaptchaToken validado server-side no auth novo; o legado ignora o campo extra.
    const model = { lognCont: username, senhCont: password, recaptchaToken };

    // Legado (IS4): o endpoint fica sob /security/ e ja devolve o envelope { success, data:{...} }
    // no formato que o componente espera, entao repassamos sem adaptar (comportamento pre-migracao).
    // Login tem indicador proprio (spinner do botao) -> suprime a barra global (NProgress).
    const skipProgress = new HttpContext().set(SKIP_PROGRESS, true);

    if (authMode === "legacy") {
      return this.http
        .post<Wrapper<any>>(
          `${this.securityAuthLegacyApi}/security/allowed-organizations-for-credential`,
          model,
          { context: skipProgress },
        )
        .pipe(
          // Normaliza o erro para a mesma forma do branch novo, evitando TypeError no componente.
          catchError((err) =>
            throwError(() => ({
              error: {
                data: {
                  error_description:
                    err?.error?.data?.error_description ??
                    err?.error?.error_description ??
                    err?.error?.message ??
                    "Usuário e(ou) senha inválidos",
                },
              },
            })),
          ),
        );
    }

    // Fluxo novo: o front NAO chama o auth direto. Passa pelo svc-security (BFF), que repassa ao auth
    // autenticando-se com o Basic do usuario de sistema cfoab.services.security. O contrato de resposta
    // e o mesmo do auth ({ organizations, services }); adaptamos aqui para o envelope/nomes legados.
    return this.http
      .post<any>(`${this.securityApi}/auth/allowed-organizations-for-credential`, model, {
        context: skipProgress,
      })
      .pipe(
        map(
          (res) =>
            ({
              success: true,
              data: {
                services: (res.services ?? []).map((s: any) => ({
                  idenServ: s.id,
                  nome: s.name,
                  // URL canonica do sistema (fallback do destino gravado na auditoria).
                  url: s.url,
                })),
                organizations: (res.organizations ?? []).map((o: any) => ({
                  idenOgan: o.organizationId,
                  nomeOgan: o.organizationName,
                  departments: (o.departments ?? []).map((d: any) => ({
                    codiSetr: d.departmentCode,
                    nomeSetr: d.departmentName,
                  })),
                })),
              },
            }) as Wrapper<any>,
        ),
        catchError((err) =>
          throwError(() => ({
            error: {
              data: {
                error_description:
                  err?.error?.errorDescription ?? "Usuário e(ou) senha inválidos",
              },
            },
          })),
        ),
      );
  }

  login(
    username: string,
    password: string,
    idenOgan: number,
    codiSetr: number,
    authMode: "new" | "legacy" = "legacy",
    targetUrl?: string,
  ): Observable<{ access_token: string; id_token: string; refresh_token: string; expires_in: number }> {
    const isNew = authMode === "new";
    // Mesma sessao de login -> barra global suprimida (indicador unico no botao).
    const skipProgress = new HttpContext().set(SKIP_PROGRESS, true);

    // Fluxo novo: o front NAO chama o connect/token do auth direto. Posta JSON ao svc-security (BFF),
    // que monta o password grant e repassa ao auth com o Basic do usuario de sistema. O svc-security
    // devolve o mesmo contrato OAuth ({ access_token, id_token, refresh_token, expires_in }).
    // targetUrl = URL de destino do sistema que o usuario esta acessando (auditoria de acesso).
    const request$ = isNew
      ? this.http.post<{
          access_token: string;
          id_token: string;
          refresh_token: string;
          expires_in: number;
        }>(
          `${this.securityApi}/auth/token`,
          { username, password, idenOgan, codiSetr, targetUrl },
          { context: skipProgress },
        )
      : // Legado (IS4): form-urlencoded para o token endpoint do auth legado. O secret do client
        // (security.ui confidential) e injetado no edge (nginx/proxy.conf.js), nunca no bundle.
        this.http.post<{
          access_token: string;
          id_token: string;
          refresh_token: string;
          expires_in: number;
        }>(
          this.securityAuthLegacyToken,
          `grant_type=password&username=${username}&password=${password}&scope=cfoab.login openid&idenOgan=${idenOgan}&codiSetr=${codiSetr}`,
          {
            headers: new HttpHeaders().set("Content-Type", "application/x-www-form-urlencoded"),
            context: skipProgress,
          },
        );

    return request$.pipe(
      tap((token) => {
        if (token.access_token) {
          // Persiste o modo ANTES de guardar os tokens: refresh/logout passam a rotear por ele.
          this.setAuthMode(authMode);
          localStorage.setItem(this.menuRouteCacheResetKey, Date.now().toString());
          this.markSessionStart();
          this.storeTokens(token, authMode);
        }
      }),
    );
  }

  // Renova access/id/refresh usando o refresh_token (grant_type=refresh_token). O OpenIddict
  // rotaciona o refresh a cada uso, por isso os tres tokens sao regravados a cada renovacao.
  refreshAccessToken(): Observable<{
    access_token: string;
    id_token: string;
    refresh_token: string;
    expires_in: number;
  }> {
    // O legado (IS4) nao emite refresh nesse fluxo: nao ha o que renovar -> interceptor faz logout.
    if (this.getAuthMode() === "legacy") {
      return throwError(() => new Error("Refresh nao suportado no auth legado."));
    }
    const refreshToken = localStorage.getItem(environment.oab_refresh_token);
    if (!refreshToken) {
      return throwError(() => new Error("Sem refresh token disponivel."));
    }
    // Refresh so ocorre no auth novo. O front posta JSON ao svc-security (BFF), que repassa o
    // refresh_token ao connect/token do auth com o Basic do usuario de sistema.
    // targetUrl = app onde o usuario esta (origin), gravado no Detalhe da auditoria do refresh.
    return this.http
      .post<{
        access_token: string;
        id_token: string;
        refresh_token: string;
        expires_in: number;
      }>(`${this.securityApi}/auth/token/refresh`, { refreshToken, targetUrl: window.location.origin })
      .pipe(
        tap((token) => {
          if (token.access_token) {
            this.storeTokens(token);
          }
        }),
      );
  }

  getAccessToken(): string {
    return this.cookieService.get(environment.oab_access_token);
  }

  getRefreshToken(): string {
    return localStorage.getItem(environment.oab_refresh_token) ?? "";
  }

  // Persiste os tokens em cookie no dominio .oab.org.br (compartilhado entre os fronts).
  //
  // Namespacing por geracao de auth:
  //   - 'new' (OpenIddict/v2) grava nas chaves v2 (oab-access-token-v2[-hom]).
  //   - 'legacy' (IS4, login broker para apps legados) grava nas chaves legadas
  //     (oab-access-token[-hom]) para que o app legado consiga ler o seu proprio token.
  // Sem isso, o token JWE do auth novo sobrescrevia o slot legado e quebrava os
  // backends legados (ex.: svc-paycenter-admin -> 401).
  private storeTokens(
    token: { access_token: string; id_token?: string; refresh_token?: string },
    mode: "new" | "legacy" = this.getAuthMode(),
  ): void {
    const accessKey =
      mode === "new" ? environment.oab_access_token : environment.oab_access_token_legacy;
    const idKey =
      mode === "new" ? environment.oab_id_token : environment.oab_id_token_legacy;

    // Access token (criptografado/JWE): vai ao backend pelo interceptor (Bearer).
    this.cookieService.put(accessKey, token.access_token, {
      domain: ".oab.org.br",
    });
    // Id token (assinado/legivel): usado pelo front para ler as claims do usuario.
    if (token.id_token) {
      this.cookieService.put(idKey, token.id_token, {
        domain: ".oab.org.br",
      });
    }
    // Refresh token: usado SO para renovar o access (lido por JS e enviado no body do
    // connect/token). Fica em localStorage, NAO em cookie: o cookie de dominio .oab.org.br
    // e enviado em TODA requisicao e o JWE de refresh estoura o buffer de header do nginx
    // (400 "Request Header Or Cookie Too Large"). Remove o cookie legado (host-only e
    // dominio compartilhado), se existir, para limpar sessoes que ainda carregam o token
    // antigo no header. Usa-se a API nativa pois CookieService nao expoe remove de chave
    // unica (so removeAll, que apagaria tambem access/id).
    const expiredAt = "Thu, 01 Jan 1970 00:00:00 GMT";
    document.cookie = `${environment.oab_refresh_token}=; expires=${expiredAt}; path=/`;
    document.cookie = `${environment.oab_refresh_token}=; expires=${expiredAt}; path=/; domain=.oab.org.br`;
    if (token.refresh_token) {
      localStorage.setItem(environment.oab_refresh_token, token.refresh_token);
    }
  }

  isLoggedIn(): boolean {
    return !!this.cookieService.get(environment.oab_access_token);
  }

  // Item 4: controle de expiração de sessão (2h absolutas a partir do login).
  markSessionStart(): void {
    localStorage.setItem(this.sessionStartKey, Date.now().toString());
  }

  isSessionExpired(): boolean {
    const start = Number(localStorage.getItem(this.sessionStartKey));
    // Sem marca de início (sessão criada antes desta regra ou em outro sistema):
    // não força expiração imediata — a contagem começa no próximo login por aqui.
    if (!start) {
      return false;
    }
    return Date.now() - start >= this.sessionMaxMs;
  }

  enforceSessionExpiration(): boolean {
    if (this.isLoggedIn() && this.isSessionExpired()) {
      this.logout();
      return true;
    }
    return false;
  }

  handleLogin() {
    this.route.navigate(["/pages/login"]);
  }

  logoutold() {
    if (environment.production) {
      this.cookieService.removeAll({ domain: ".oab.org.br" });
    } else {
      this.cookieService.removeAll();
    }

    this.route.navigate(["/pages/login"]);
  }
  
  logout() {
    // Fecha qualquer modal aberto antes de redirecionar para o login. Sem isso, um modal
    // (ex.: cadastro de endpoint) permanece visivel sobre a tela de login quando o usuario
    // e deslogado por 401 ou expiracao de sessao.
    this.closeAllModals();

    this.cookieService.removeAll({ path: "/" });
    this.cookieService.removeAll({ path: "/", domain: ".oab.org.br" });
    this.cookieService.removeAll({ path: "/", domain: "seguranca-hom.oab.org.br" });

    localStorage.clear();
    sessionStorage.clear();

    this.route.navigate(["/pages/login"]);
  }

  // Remove os cookies de auth compartilhados (host atual + dominio .oab.org.br + host de hom).
  // Diferente do logout(): NAO limpa localStorage/sessionStorage nem navega — usado para recuperar
  // do "Request header or cookie too large" (cookies legados estouraram o buffer do gateway) sem
  // perder o guard anti-loop guardado em sessionStorage.
  clearSharedCookies(): void {
    this.cookieService.removeAll({ path: "/" });
    this.cookieService.removeAll({ path: "/", domain: ".oab.org.br" });
    this.cookieService.removeAll({ path: "/", domain: "seguranca-hom.oab.org.br" });
  }

  // Remove modais e backdrops do ngx-bootstrap diretamente do DOM.
  //
  // O BsModalService nao serve aqui: cada modulo de feature (servico, vincular-*) importa o
  // ModalModule.forRoot() e e carregado lazy, entao tem a sua propria instancia do servico.
  // O AuthService (root) injetaria uma instancia diferente daquela que abriu o modal, e o
  // hide() nao teria efeito. A limpeza no DOM cobre qualquer modal aberto, independente do
  // modulo de origem. Como o logout encerra a sessao inteira, dispensar os modais sem os
  // callbacks de onHidden e aceitavel.
  private closeAllModals(): void {
    document
      .querySelectorAll("modal-container, bs-modal-backdrop, .modal-backdrop")
      .forEach((el) => el.remove());

    // O ngx-bootstrap trava o scroll do body enquanto ha modal aberto; restaura o estado.
    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("padding-right");
    document.body.style.removeProperty("overflow");
  }

  organizacoesSetores(idenCont) {
    return this.http.get<any>(
      `${this.securityApi}/authorization/autorizacao-organizacao?idenCont=${idenCont}`,
    );
  }
}
